import { useEffect, useState, useRef, useCallback } from "react";
import { Container, TextField, Button, Grid, Typography, ButtonGroup, CardHeader, MenuItem } from "@mui/material";
import { useAuth } from "../hooks/useAuth";
import { GetTokenIdentity } from "../utils/Token";
import { Icons } from "../utils/Icon";
import { CustomStyles } from "../utils/Styled";

import axios from "axios";
import MaterialTable from "material-table";

import CreateUtilityLicenseForm from "../components/forms/utilityLicense/Create";
import EditUtilityLicenseForm from "../components/forms/utilityLicense/Edit";

export const UtilityLicensePage = () => {
    const { token, logout } = useAuth();
    const identity = GetTokenIdentity(token);

    const classes = CustomStyles;

    const licenseKeyInputRef = useRef(null);
    const companyNameInputRef = useRef(null);
    const contactInputRef = useRef(null);
    const macAddressInputRef = useRef(null);

    const [data, setData] = useState([]);
    const [count, setCount] = useState(0);
    const [page, setPage] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [filter, setFilter] = useState({
        license_key: "",
        company_name: "",
        contact: "",
        mac_address: "",
        status: "all",
    });
    const [sort] = useState({});

    const [open, setOpen] = useState({
        create: false,
        edit: false,
    });

    const [selectedRow, setSelectedRow] = useState(null);

    const handleOpen = (type, row) => {
        setOpen({ ...open, [type]: true });
        setSelectedRow(row);
    };

    const handleClose = (type) => {
        setOpen({ ...open, [type]: false });
        setSelectedRow(null);
    };

    const handleResetFilter = () => {
        setPage(0);
        setFilter({
            license_key: "",
            company_name: "",
            contact: "",
            mac_address: "",
            status: "all",
        });
    };

    const handleFilterChange = (event) => {
        setPage(0);
        setFilter({ ...filter, [event.target.name]: event.target.value });
    };

    const fetchData = useCallback(async () => {
        try {
            const { data } = await axios.get(process.env.REACT_APP_WEBSITE_BACKEND_URL + "api/v1/utility-license", {
                params: {
                    page: page + 1,
                    perPage,
                    filter: JSON.stringify(filter),
                    sort,
                },
            });

            setData(data.data.utilityLicenses || []);
            setCount(Number(data.data.total) || 0);
        } catch (error) {
            if (error.response && error.response.status === 401) {
                logout("Your account has been logged out of a session.");
            } else {
                console.log(error);
            }
        }
    }, [page, perPage, filter, sort, logout]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const columns = [
        {
            title: "License Key",
            field: "license_key",
            cellStyle: { width: 180, maxWidth: 180 },
            headerStyle: { width: 180, maxWidth: 180 },
        },
        {
            title: "Expiry Date",
            field: "expiry_date",
            cellStyle: { width: 140, maxWidth: 140 },
            headerStyle: { width: 140, maxWidth: 140 },
        },
        { title: "Company Name", field: "company_name" },
        { title: "Contact", field: "contact" },
        { title: "Mac Address", field: "mac_address" },
    ];

    return (
        <div>
            <div className={classes.background} />
            <Container maxWidth="xl" sx={{ py: 2 }} style={{ padding: 14 }}>
                {identity.user.role_id === 1 || identity.user.role_id === 2 ? (
                    <MaterialTable
                        title="Utility License List"
                        icons={Icons}
                        columns={columns}
                        data={data}
                        page={page}
                        totalCount={count}
                        style={{ padding: "24px" }}
                        options={{
                            pageSize: perPage,
                            pageSizeOptions: [10, 20, 30, 50],
                            search: false,
                            filtering: false,
                            sorting: false,
                            actionsColumnIndex: -1,
                            headerStyle: {
                                fontWeight: "bold",
                                backgroundColor: "#f1f1f1",
                                color: "#212122",
                            },
                            maxBodyHeight: "600px",
                            paginationType: "stepped",
                            padding: "dense",
                            draggable: false,
                        }}
                        localization={{
                            pagination: {
                                labelRowsPerPage: "",
                                labelDisplayedRows: "{from}-{to} of {count}",
                                labelRowsSelect: "rows",
                            },
                            toolbar: {
                                nRowsSelected: "{0} row(s) selected",
                            },
                            header: {
                                actions: "Actions",
                            },
                            body: {
                                emptyDataSourceMessage: "No data to display",
                            },
                        }}
                        actions={[
                            {
                                icon: () => <Icons.Edit />,
                                tooltip: "Edit Utility License Information",
                                onClick: (event, rowData) => handleOpen("edit", rowData),
                            },
                        ]}
                        onChangePage={(nextPage) => setPage(nextPage)}
                        onChangeRowsPerPage={(nextPerPage) => setPerPage(nextPerPage)}
                        components={{
                            Toolbar: () => (
                                <>
                                    <div>
                                        <Grid container spacing={2} sx={{ px: 0.1, pb: 3.5 }}>
                                            <Grid item xs={12} sm={3}>
                                                <CardHeader
                                                    sx={{ p: 0 }}
                                                    title={
                                                        <Typography component="h1" variant="h5" color="primary" fontWeight="bold">
                                                            Utility License List
                                                        </Typography>
                                                    }
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={9} style={{ textAlign: "right" }}>
                                                <ButtonGroup>
                                                    <Button
                                                        onClick={() => handleOpen("create", null)}
                                                        variant="outlined"
                                                        startIcon={<Icons.AddIcon fontWeight="bold" />}
                                                        color="primary"
                                                        sx={{
                                                            fontWeight: 800,
                                                            paddingTop: 0.5,
                                                            paddingBottom: 0.5,
                                                        }}
                                                    >
                                                        Create
                                                    </Button>
                                                </ButtonGroup>
                                            </Grid>
                                        </Grid>
                                    </div>

                                    <form>
                                        <Grid container spacing={2} sx={{ paddingBottom: 1.5 }}>
                                            <Grid item xs={12} sm={2}>
                                                <TextField
                                                    autoComplete="off"
                                                    fullWidth
                                                    label="License Key"
                                                    name="license_key"
                                                    variant="outlined"
                                                    autoFocus={licenseKeyInputRef.current === document.activeElement}
                                                    value={filter.license_key}
                                                    onChange={handleFilterChange}
                                                    inputRef={licenseKeyInputRef}
                                                    size="small"
                                                    InputLabelProps={{
                                                        shrink: true,
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={2}>
                                                <TextField
                                                    autoComplete="off"
                                                    fullWidth
                                                    label="Company Name"
                                                    name="company_name"
                                                    variant="outlined"
                                                    autoFocus={companyNameInputRef.current === document.activeElement}
                                                    value={filter.company_name}
                                                    onChange={handleFilterChange}
                                                    inputRef={companyNameInputRef}
                                                    size="small"
                                                    InputLabelProps={{
                                                        shrink: true,
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={2}>
                                                <TextField
                                                    autoComplete="off"
                                                    fullWidth
                                                    label="Contact"
                                                    name="contact"
                                                    variant="outlined"
                                                    autoFocus={contactInputRef.current === document.activeElement}
                                                    value={filter.contact}
                                                    onChange={handleFilterChange}
                                                    inputRef={contactInputRef}
                                                    size="small"
                                                    InputLabelProps={{
                                                        shrink: true,
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={2}>
                                                <TextField
                                                    autoComplete="off"
                                                    fullWidth
                                                    label="Mac Address"
                                                    name="mac_address"
                                                    variant="outlined"
                                                    autoFocus={macAddressInputRef.current === document.activeElement}
                                                    value={filter.mac_address}
                                                    onChange={handleFilterChange}
                                                    inputRef={macAddressInputRef}
                                                    size="small"
                                                    InputLabelProps={{
                                                        shrink: true,
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={2}>
                                                <TextField
                                                    fullWidth
                                                    select
                                                    label="Status"
                                                    variant="outlined"
                                                    name="status"
                                                    value={filter.status}
                                                    onChange={handleFilterChange}
                                                    size="small"
                                                    InputLabelProps={{
                                                        shrink: true,
                                                    }}
                                                >
                                                    <MenuItem value="all">All</MenuItem>
                                                    <MenuItem value="active">Active</MenuItem>
                                                    <MenuItem value="expired">Expired</MenuItem>
                                                    <MenuItem value="expiring_30_days">Expiring in 30 Days</MenuItem>
                                                    <MenuItem value="expiring_90_days">Expiring in 90 Days</MenuItem>
                                                </TextField>
                                            </Grid>
                                            <Grid item xs={12} sm={1}>
                                                <Button
                                                    fullWidth
                                                    variant="contained"
                                                    color="error"
                                                    sx={{ color: "#ffffff", marginTop: "2px" }}
                                                    onClick={handleResetFilter}
                                                >
                                                    Reset
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    </form>
                                </>
                            ),
                        }}
                    />
                ) : (
                    <Typography variant="subtitle2">You don't have permission to view utility licenses.</Typography>
                )}
                <CreateUtilityLicenseForm open={open.create} handleClose={() => handleClose("create")} fetchData={fetchData} />
                <EditUtilityLicenseForm open={open.edit} handleClose={() => handleClose("edit")} rowData={selectedRow} fetchData={fetchData} />
            </Container>
        </div>
    );
};